import React from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useSelector } from 'react-redux'

import ModalForm from 'app/components/ModalForm'
import { confirm } from 'app/helpers'
import {
  dispatchUpdateReorderAnnouncementModalState,
  dispatchFetchAnnouncementsForReorder,
  dispatchUpdateAnnouncementOrder,
  dispatchSaveAnnouncementOrder
} from 'app/store/dispatcher'
import {
  selectIsReorderAnnouncementModalOpen,
  selectReorderAnnouncementData,
  selectIsAnnouncementRequestInProgress
} from 'app/store/selectors'

import {
  Container,
  InstructionText,
  DragDropContainer,
  AnnouncementCard,
  CardContent,
  HamburgerIcon,
  SequenceNumber,
  StatusContainer,
  AnnouncementInfo,
  Title,
  Type,
  Centres,
  StatusBadge,
  StatusDot,
  LoadingContainer
} from './styles'

const ReorderAnnouncement = () => {
  const isReorderModalOpen = useSelector(selectIsReorderAnnouncementModalOpen)
  const reorderData = useSelector(selectReorderAnnouncementData)
  const isRequestInProgress = useSelector(selectIsAnnouncementRequestInProgress)

  const handleModalClose = () => {
    dispatchUpdateReorderAnnouncementModalState(false)
  }

  const handleSaveOrder = () => {
    if (!reorderData?.results) return

    const orderData = reorderData.results.map((announcement, index) => ({
      sequence: index + 1,
      announcement: announcement.uuid
    }))

    // Show confirmation modal before saving
    confirm(
      () => {
        dispatchSaveAnnouncementOrder(orderData)
      },
      'Are you sure you want to save the new announcement order?',
      () => {},
      {
        confirmButtonText: 'confirm',
        cancelButtonText: 'cancel'
      }
    )
  }

  const handleDragEnd = (result) => {
    if (!result.destination || !reorderData?.results) return

    const items = Array.from(reorderData.results)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    dispatchUpdateAnnouncementOrder({
      ...reorderData,
      results: items
    })
  }

  React.useEffect(() => {
    if (isReorderModalOpen) {
      // Fetch all announcements for reordering
      dispatchFetchAnnouncementsForReorder({
        limit: 1000,
        exclude_status: 'lapsed',
        order_by_sequence: 'True'
      })
    }
  }, [isReorderModalOpen])

  return (
    <ModalForm
      isModalOpen={isReorderModalOpen}
      onModalClose={handleModalClose}
      modalTitle="set the sequence"
      enableSave={true}
      buttonLabel="Save"
      onSaveClick={handleSaveOrder}
      loading={isRequestInProgress}
      maxWidth="md">
      <Container>
        <InstructionText>
          Adjust the sequence of announcements by dragging them into place
        </InstructionText>
        {reorderData?.results ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="announcements">
              {(provided) => (
                <DragDropContainer
                  {...provided.droppableProps}
                  ref={provided.innerRef}>
                  {reorderData.results.map((announcement, index) => (
                    <Draggable
                      key={announcement.uuid}
                      draggableId={announcement.uuid}
                      index={index}>
                      {(provided, snapshot) => (
                        <AnnouncementCard
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={provided.draggableProps.style}
                          isDragging={snapshot.isDragging}>
                          <CardContent>
                            <HamburgerIcon {...provided.dragHandleProps}>
                              ☰
                            </HamburgerIcon>
                            <SequenceNumber>{index + 1}</SequenceNumber>
                            <AnnouncementInfo>
                              <Title>{announcement.title}</Title>
                              <StatusContainer>
                                <StatusBadge
                                  status={announcement.status ?? null}>
                                  <StatusDot
                                    status={announcement.status ?? null}
                                  />
                                  {announcement.status}
                                </StatusBadge>
                                •<Type>{announcement.type}</Type>•
                                <Centres>
                                  {announcement.centres
                                    ?.map((centre) => centre.name)
                                    .join(', ') || 'No centres'}
                                </Centres>
                              </StatusContainer>
                            </AnnouncementInfo>
                          </CardContent>
                        </AnnouncementCard>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </DragDropContainer>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <LoadingContainer>Loading announcements...</LoadingContainer>
        )}
      </Container>
    </ModalForm>
  )
}

export default ReorderAnnouncement
